import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store';
import { Icon } from '../icons';
import './Header.css';

const ADMIN_MENU_ITEMS = [
  { path: '/admin/users', icon: 'users', label: 'User Management' },
  { path: '/admin/holidays', icon: 'calendar', label: 'Manage Holidays' },
  { path: '/admin/leave-policies', icon: 'clipboardText', label: 'Leave Policies' },
  { path: '/admin/report', icon: 'chartBar', label: 'Report' },
];

export default function Header() {
  const { user, toggleSidebar, toggleMobileNav, logout } = useStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : 'U';

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  // Close the dropdown on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKeyDown = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login');
  };

  // Below the mobile breakpoint the sidebar is an off-canvas drawer (opened/
  // closed via mobileNavOpen); above it, the same button collapses the
  // sidebar to an icon rail (sidebarCollapsed) instead.
  const handleMenuClick = () => {
    if (window.matchMedia('(max-width: 768px)').matches) {
      toggleMobileNav();
    } else {
      toggleSidebar();
    }
  };

  return (
    <header className="app-header">
      <div className="header-left">
        <button className="header-menu-btn" onClick={handleMenuClick} aria-label="Toggle sidebar">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <span className="header-date">{today}</span>
      </div>

      <div className="header-right">
        <div className="header-role-badge">
          <span className="badge badge-violet capitalize">
            {user?.role}
          </span>
          {user?.employee_type === 'contractor' && (
            <span className="badge badge-amber" style={{ marginLeft: 6 }}>Contractor</span>
          )}
        </div>

        <div className="header-profile-wrap" ref={menuRef}>
          <button
            className="header-profile-btn"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Account menu"
            aria-expanded={menuOpen}
          >
            <div className="avatar avatar-md" style={{ background: 'var(--brand-violet)' }}>
              {user?.avatar_url
                ? <img src={user.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                : initials
              }
            </div>
            <Icon name="chevronDown" size={14} style={{ color: 'var(--text-secondary)', transition: 'transform var(--transition)', transform: menuOpen ? 'rotate(180deg)' : 'none' }} />
          </button>

          {menuOpen && (
            <div className="header-profile-menu">
              <div className="header-profile-menu-user">
                {user?.avatar_url
                  ? <div className="avatar avatar-lg" style={{ background: 'var(--brand-violet)' }}><img src={user.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /></div>
                  : (
                    <svg width="40" height="40" fill="currentColor" viewBox="0 0 256 256" style={{ color: 'var(--brand-violet)', flexShrink: 0 }}>
                      <path d="M224,128a95.76,95.76,0,0,1-31.8,71.37A72,72,0,0,0,128,160a40,40,0,1,0-40-40,40,40,0,0,0,40,40,72,72,0,0,0-64.2,39.37h0A96,96,0,1,1,224,128Z" opacity="0.2"></path>
                      <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24ZM74.08,197.5a64,64,0,0,1,107.84,0,87.83,87.83,0,0,1-107.84,0ZM96,120a32,32,0,1,1,32,32A32,32,0,0,1,96,120Zm97.76,66.41a79.66,79.66,0,0,0-36.06-28.75,48,48,0,1,0-59.4,0,79.66,79.66,0,0,0-36.06,28.75,88,88,0,1,1,131.52,0Z"></path>
                    </svg>
                  )
                }
                <div className="header-profile-menu-user-info">
                  <span className="header-profile-menu-name">{user?.first_name} {user?.last_name}</span>
                  <span className="header-profile-menu-email">{user?.email}</span>
                </div>
              </div>
              <div className="header-profile-menu-divider" />
              <button className="header-profile-menu-item" onClick={() => { setMenuOpen(false); navigate('/profile'); }}>
                <span className="header-profile-menu-icon"><Icon name="profile" /></span> My Profile
              </button>
              {user?.role === 'admin' && ADMIN_MENU_ITEMS.map((item) => (
                <button key={item.path} className="header-profile-menu-item" onClick={() => { setMenuOpen(false); navigate(item.path); }}>
                  <span className="header-profile-menu-icon"><Icon name={item.icon} /></span> {item.label}
                </button>
              ))}
              <div className="header-profile-menu-divider" />
              <button className="header-profile-menu-item header-profile-menu-item--danger" onClick={handleLogout}>
                <span className="header-profile-menu-icon"><Icon name="signOut" /></span> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
